import { useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { useEffect, useRef } from "react";
import axios from "axios";
import { currentUserChanged } from "@/entities/current-user";
import { User } from "@/features/user";
import { getAccounts } from "@/actions/skedCore";
import type { AnalyticsUser } from "@/services/analytics-init";

const APP_VERSION = process.env.SKED_APP_BUILD_HASH || "unknown";

export interface BootstrapRedirect {
  type: "bootstrap_redirect";
  reason: string;
  redirectTo: string;
  message?: string;
}

export class BootstrapRedirectError extends Error {
  constructor(public readonly redirect: BootstrapRedirect) {
    super(`SPA bootstrap requires redirect: ${redirect.reason}`);
    this.name = "BootstrapRedirectError";
  }
}

export function getBootstrapRedirect(error: unknown): BootstrapRedirect | null {
  const response = (
    error as {
      response?: {
        status?: number;
        data?: Partial<BootstrapRedirect>;
      };
    }
  )?.response;
  if (
    response?.status !== 401 &&
    response?.status !== 403 &&
    response?.status !== 409
  ) {
    return null;
  }
  const data = response.data;

  return data?.type === "bootstrap_redirect" &&
    typeof data.reason === "string" &&
    typeof data.redirectTo === "string" &&
    data.redirectTo.startsWith("/") &&
    !data.redirectTo.startsWith("//")
    ? (data as BootstrapRedirect)
    : null;
}

export async function fetchInitialUserData(): Promise<User> {
  try {
    const response = await axios.get("/user/bootstrap");
    if (response.status !== 200) {
      throw new Error("Failed to fetch user data");
    }
    return response.data;
  } catch (error) {
    const redirect = getBootstrapRedirect(error);
    if (redirect) throw new BootstrapRedirectError(redirect);
    throw error;
  }
}

/**
 * Map User to AnalyticsUser format
 */
function mapUserToAnalyticsUser(user: User): AnalyticsUser {
  return {
    userId: user.userId,
    email: user.email,
    adminId: user.adminId,
    company: user.company,
    plan: user.plan,
    role: user.role,
    timezone: user.timezone,
    user_status: user.user_status ?? "unknown",
    mrr: user.subscription?.subscription?.mrr,
    currency: user.currency,
    createdAt: user.subscription?.subscription?.created_at
      ? new Date(user.subscription.subscription.created_at * 1000).toISOString()
      : undefined,
    timeDatePickerPref: user.preferences?.timeDatePickerPref ?? undefined,
    timePreference: user.preferences?.timePreference,
  };
}

/**
 * Hook to fetch and initialize user data on app startup.
 * This replaces the old Angular $rootScope.user initialization.
 *
 * - Loads analytics scripts early (before user fetch)
 * - Initializes Amplitude SDK
 * - Fetches user data from /user endpoint
 * - Synchronizes current user state into the registered app store
 * - Loads accounts via getAccounts action
 * - Initializes all analytics services with user data
 *
 * Analytics and observability modules are dynamically imported so the
 * heavy @amplitude, @openobserve, and @opentelemetry bundles are not
 * included in the initial synchronous chunk.
 */
export function useInitialUserData() {
  const dispatch = useDispatch();
  const analyticsInitializedRef = useRef(false);
  const accountsRequestedRef = useRef(false);

  // Load analytics scripts and initialize Amplitude early (before user fetch).
  // Dynamic import keeps ~500 KB of @amplitude out of the initial bundle.
  useEffect(() => {
    void import("@/services/analytics-init")
      .then(({ setAppVersion, loadAnalyticsScripts, initializeAmplitude }) => {
        setAppVersion(APP_VERSION);
        loadAnalyticsScripts();
        initializeAmplitude();
      })
      .catch((error) => {
        console.warn(
          "[useInitialUserData] failed to initialize analytics scripts (non-fatal)",
          error
        );
      });
  }, [dispatch]);

  const query = useQuery({
    queryKey: ["initial-user"],
    queryFn: fetchInitialUserData,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) =>
      !(error instanceof BootstrapRedirectError) && failureCount < 1,
  });

  useEffect(() => {
    if (query.data) {
      const user = query.data;

      // Keep the registered app store's current-user helpers in sync first.
      currentUserChanged(user);

      // /user hydrates the session fields used by /accounts. This ordering also
      // makes startup safe once index.html is served without renderIndex.
      if (!accountsRequestedRef.current) {
        accountsRequestedRef.current = true;
        dispatch(getAccounts());
      }

      // Initialize all analytics services with user data (Amplitude, GA, HubSpot, etc.)
      // This is only done once per app lifecycle
      if (!analyticsInitializedRef.current) {
        analyticsInitializedRef.current = true;
        const analyticsUser = mapUserToAnalyticsUser(user);

        void import("@/services/analytics-init")
          .then(
            ({
              initializeAnalytics,
              initializeAmplitudeExperiment,
              fireSignupVerifiedConversion,
            }) => {
              initializeAnalytics(analyticsUser);
              void initializeAmplitudeExperiment(analyticsUser);

              // SKE-3699 Phase 5: if this dashboard load is the redirect target of a
              // successful email verification (?signup_verified=1), fire the
              // client-side signup-completed conversion push once. No-op otherwise
              // (and in shadow mode the param is never present). Dedupes against the
              // authoritative server-side event via the shared transaction_id.
              fireSignupVerifiedConversion({
                userId: user.userId,
                plan: user.plan,
                email: user.email,
                transactionId: user.subscription?.subscription?.id,
              });
            }
          )
          .catch((error) => {
            console.warn(
              "[useInitialUserData] failed to initialize analytics user context (non-fatal)",
              error
            );
          });

        // Set user context on OpenObserve RUM (independent of analyticsEnabled)
        void import("@/utils/observability")
          .then(({ setObservabilityUser }) => {
            setObservabilityUser({
              userId: user.userId,
              adminId: user.adminId,
              email: user.email,
              company: user.company,
              plan: user.plan,
              role: user.role,
            });
          })
          .catch((error) => {
            console.warn(
              "[useInitialUserData] failed to initialize observability user context (non-fatal)",
              error
            );
          });
      }
    }
  }, [query.data, dispatch]);

  return {
    user: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    redirectTo:
      query.error instanceof BootstrapRedirectError
        ? query.error.redirect.redirectTo
        : null,
  };
}
